import React, { useEffect, useState } from "react";
import { SignalPulse } from "../components/SignalPulse";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { MessageSquare, Link2, Loader2, ArrowLeft, Pencil, Lock, Flag, Instagram, Sparkles, MessageSquareQuote, Layers, Radio, Ban, Flame, Compass, ShieldCheck } from "lucide-react";
import { glowTarget } from "../lib/guideGlow";
import api from "../lib/api";
import { Avatar } from "../components/Media";
import { CategoryPill, PostCard } from "./Feed";
import { useAuth } from "../context/AuthContext";
import { useCrypto } from "../context/CryptoContext";
import { DisturbanceModal } from "../components/DisturbanceModal";
import { ResonancePoints } from "../components/ResonancePoints";
import { FavoriteButton } from "../components/FavoriteButton";
import { InnerCircle } from "../components/InnerCircle";
import EditProfile from "./EditProfile";
import { NotificationsToggle } from "../components/NotificationsToggle";
import { CoachTips } from "../components/CoachTip";
import { useOrientation } from "../components/OrientationTour";
import { CollectionCard, NewCollectionControl, useCollections } from "../components/CollectionsSection";

const Block = ({ label, children }) => (
  <div>
    <div className="font-body text-[10px] font-medium text-zinc-500 tracking-[0.2em] uppercase mb-2">{label}</div>
    <p className="font-body text-sm text-zinc-300 leading-relaxed ugc-text">{children}</p>
  </div>
);

const Locked = ({ label }) => (
  <div>
    <div className="font-body text-[10px] font-medium text-zinc-500 tracking-[0.2em] uppercase mb-2">{label}</div>
    <p className="font-body text-xs text-zinc-600 flex items-center gap-1"><Lock size={12} /> Encrypted - unlock to view</p>
  </div>
);

export default function Profile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { openTour } = useOrientation();
  const { decryptNetText } = useCrypto();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [flagging, setFlagging] = useState(false);
  const [dec, setDec] = useState({});
  const [fav, setFav] = useState(false);
  const [focusResonance, setFocusResonance] = useState(false);
  const { collections, reload: reloadCollections } = useCollections(id);
  const [tab, setTab] = useState("posts");
  const [streamDetail, setStreamDetail] = useState(null);

  const wantsPhraseCheck = new URLSearchParams(location.search).get("checkphrase") === "1";
  useEffect(() => {
    if (wantsPhraseCheck && profile && user?.id === profile.id) setEditing(true);
  }, [wantsPhraseCheck, profile, user?.id]);

  const load = () => {
    api.get(`/members/${id}`).then(({ data }) => { setProfile(data); setFav(!!data.favorited); }).catch(() => setProfile(false));
  };

  useEffect(() => {
    setProfile(null);
    setDec({});
    setTab("posts");
    setStreamDetail(null);
    load();
    /* eslint-disable-next-line */
  }, [id]);

  // decrypt encrypted profile fields
  useEffect(() => {
    (async () => {
      if (!profile || !profile.enc_fields) return;
      const out = {};
      for (const f of ["bio", "offering", "seeking", "link", "instagram"]) {
        const ef = profile.enc_fields[f];
        if (ef && ef.ct) {
          const pt = await decryptNetText(ef.ct, ef.iv);
          out[f] = pt === null ? "__LOCKED__" : pt;
        }
      }
      setDec(out);
    })();
    /* eslint-disable-next-line */
  }, [profile]);

  // Deep-link from the invite nudge: scroll to & briefly highlight the Resonance hub.
  useEffect(() => {
    if (!profile || user?.id !== profile.id) return;
    const params = new URLSearchParams(location.search);
    if (params.get("focus") !== "resonance") return;
    const t = setTimeout(() => {
      const el = document.getElementById("resonance-anchor");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setFocusResonance(true);
        setTimeout(() => setFocusResonance(false), 2600);
      }
    }, 450);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [profile, location.search]);

  // Lazily load the member's mini-stream items when the Conscious Stream tab is opened.
  useEffect(() => {
    if (tab !== "stream") return;
    const sc = (collections || []).find((c) => c.system === "stream");
    if (!sc) return;
    if (streamDetail && streamDetail._cid === sc.id && streamDetail._cnt === sc.post_count) return;
    api.get(`/collections/${sc.id}`).then(({ data }) => setStreamDetail({ ...data, _cid: sc.id, _cnt: sc.post_count })).catch(() => setStreamDetail({ _cid: sc.id, _cnt: sc.post_count, posts: [] }));
    /* eslint-disable-next-line */
  }, [tab, collections]);

  if (profile === null) return <div className="flex justify-center py-32"><SignalPulse size={44} /></div>;
  if (profile === false) return <div className="text-center py-32 font-body text-zinc-500">Sovereign not found.</div>;

  const isMe = user?.id === profile.id;
  const fieldVal = (f) => (f in dec ? dec[f] : profile[f]);

  const deletePost = async (postId) => {
    const prev = profile;
    setProfile({ ...profile, posts: profile.posts.filter((p) => p.id !== postId), posts_count: profile.posts_count - 1 });
    try {
      await api.delete(`/posts/${postId}`);
    } catch {
      setProfile(prev);
      alert("Could not delete the post. Please try again.");
    }
  };

  const deleteStreamPost = async (postId) => {
    try { await api.delete(`/posts/${postId}`); setStreamDetail(null); reloadCollections(); }
    catch { alert("Could not delete the post. Please try again."); }
  };

  const blockMember = async () => {
    if (!window.confirm(`Block ${profile.name}? They won't see you or your posts, and neither of you can message the other. This is private, they're never told, and the Council isn't involved.`)) return;
    try { await api.post("/blocks", { member_id: profile.id }); load(); }
    catch { alert("Could not block this member. Please try again."); }
  };

  const unblockMember = async () => {
    try { await api.delete(`/blocks/${profile.id}`); load(); }
    catch { alert("Could not unblock. Please try again."); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
      {editing && <EditProfile profile={profile} decrypted={dec} onClose={() => setEditing(false)} onSaved={load} />}
      {flagging && <DisturbanceModal target={{ type: "profile", id: profile.id, authorId: profile.id, snippet: `${profile.name}: ${profile.headline || ""}` }} onClose={() => setFlagging(false)} />}
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-zinc-500 hover:text-white text-xs mb-8 font-body"><ArrowLeft size={14} /> Back</button>

      <CoachTips screen={isMe ? "profile-own" : "profile-other"} />

      <div className="grid xl:grid-cols-3 gap-10">
        <aside className="xl:col-span-1 xl:sticky xl:top-8 self-start space-y-6">
          <Avatar name={profile.name} src={profile.avatar} size={96} />
          <div>
            <h1 className="font-heading text-2xl font-medium tracking-tight text-white break-words">{profile.name}</h1>
            {profile.council_role_label && (
              <div className="mt-1 inline-flex items-center gap-1.5 border border-[#A855F7]/50 bg-[#A855F7]/10 text-[#C084FC] px-2.5 py-1" data-testid="council-title-badge">
                <Sparkles size={12} />
                <span className="font-body text-[10px] tracking-[0.18em] uppercase">Council · {profile.council_role_label}</span>
              </div>
            )}
            {profile.beacon_handle && (
              <button onClick={() => navigate(`/beacon/view/${profile.beacon_handle}`)} data-testid="profile-beacon-badge" title="Visit this Beacon" className="flex w-fit items-center gap-2.5 text-[#14A89A] bg-[#14A89A]/10 hover:bg-[#14A89A]/20 hover:-translate-y-0.5 transition-[background-color,transform] duration-200 mt-4 border border-[#14A89A]/60 pl-4 pr-5 py-2.5 rounded-full shadow-[0_0_18px_rgba(20,168,154,0.18)]">
                <Flame size={20} /> <span className="font-body text-sm font-medium tracking-[0.18em] uppercase">Beacon</span>
              </button>
            )}
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5" data-testid="profile-domains">
              {(profile.domains?.length ? profile.domains : [profile.contribution_type]).filter(Boolean).map((d) => <CategoryPill key={d} cat={d} />)}
            </div>
            <p className="font-body text-base text-zinc-300 mt-4">{profile.headline}</p>
          </div>

          {isMe ? (
            <div className="space-y-4">
              {!user?.mfa_enabled && (
                <button onClick={() => { setEditing(true); glowTarget('[data-testid="mfa-settings"]', { missing: null }); }} data-testid="profile-protect-account"
                  className="w-full flex items-center gap-2.5 border border-amber-300/30 bg-amber-300/[0.04] px-4 py-2.5 text-left hover:bg-amber-300/[0.08] transition-colors">
                  <ShieldCheck size={16} className="text-amber-300 shrink-0" />
                  <span className="font-body text-xs text-zinc-300"><span className="text-white font-medium">Protect your account.</span> Turn on two-step sign-in, it takes 1 minute.</span>
                </button>
              )}
              <button onClick={() => setEditing(true)} data-testid="edit-profile-button" className="w-full border border-white/20 text-white font-medium px-6 py-3 hover:bg-white/5 transition-colors flex items-center justify-center gap-2 font-body">
                <Pencil size={16} /> Edit Profile
              </button>
              <button onClick={openTour} data-testid="profile-take-tour-button" className="w-full border border-[#A855F7]/40 text-[#A855F7] font-medium px-6 py-3 hover:bg-[#A855F7]/10 transition-colors flex items-center justify-center gap-2 font-body text-sm">
                <Compass size={16} /> Take the tour
              </button>
              <button onClick={() => navigate("/beacon")} data-testid="beacon-profile-button" className={`w-full border font-medium px-6 py-3 transition-colors flex items-center justify-center gap-2 font-body ${user?.beacon_nudge ? "border-[#14A89A] text-[#14A89A] bg-[#14A89A]/10 animate-pulse ring-2 ring-[#14A89A]/50" : "border-[#14A89A]/40 text-[#14A89A] hover:bg-[#14A89A]/10"}`}>
                <Flame size={16} /> {user?.beacon_nudge ? "Beacon approved, set it up ✨" : "Beacon Profile"}
              </button>
              <NotificationsToggle />
              <div id="resonance-anchor" className={`transition-all duration-500 ${focusResonance ? "ring-2 ring-[#14A89A] ring-offset-4 ring-offset-[#0A0A0A]" : ""}`}>
                <ResonancePoints />
              </div>
              <button onClick={() => navigate("/voices")} data-testid="profile-voices-button" className="w-full border border-white/15 text-zinc-300 font-medium px-6 py-3 hover:border-[#14A89A] hover:text-white transition-colors flex items-center justify-center gap-2 font-body text-sm">
                <MessageSquareQuote size={16} /> Sovereign Voices
              </button>
            </div>
          ) : profile.blocked_by_me ? (
            <div className="space-y-3" data-testid="profile-blocked-actions">
              <div className="w-full border border-white/15 px-6 py-4 text-center">
                <Ban className="mx-auto text-zinc-500 mb-2" size={20} />
                <p className="font-body text-sm text-zinc-300">You've blocked this member.</p>
              </div>
              <button onClick={unblockMember} data-testid="unblock-member-button" className="w-full border border-[#14A89A] text-[#14A89A] font-medium px-6 py-3 hover:bg-[#14A89A] hover:text-[#0A0A0A] transition-colors font-body">Unblock</button>
            </div>
          ) : profile.blocked ? (
            <div className="w-full border border-white/15 px-6 py-4 text-center" data-testid="profile-unavailable">
              <p className="font-body text-sm text-zinc-400">This member is unavailable.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <button onClick={() => navigate(`/messages/${profile.id}`)} data-testid="message-member-button" className="w-full border border-[#14A89A] text-[#14A89A] font-medium px-6 py-3 hover:bg-[#14A89A] hover:text-[#0A0A0A] transition-colors flex items-center justify-center gap-2 font-body">
                <MessageSquare size={16} /> Open Exchange
              </button>
              <div data-testid="profile-favorite-row" className="w-full border border-white/15 px-6 py-3 flex items-center justify-between gap-3 font-body">
                <span className="text-sm text-zinc-300">{fav ? "In your Inner Circle" : "Add to your Inner Circle"}</span>
                <FavoriteButton memberId={profile.id} initial={profile.favorited} size={20} onChange={setFav} />
              </div>
              <div className="flex items-center justify-center gap-4">
                <button onClick={() => setFlagging(true)} data-testid="profile-flag-button" className="text-zinc-600 hover:text-[#A855F7] font-body text-xs flex items-center gap-1.5 transition-colors">
                  <Flag size={12} /> Report a disturbance
                </button>
                <span className="text-zinc-700">·</span>
                <button onClick={blockMember} data-testid="block-member-button" className="text-zinc-600 hover:text-red-500 font-body text-xs flex items-center gap-1.5 transition-colors">
                  <Ban size={12} /> Block member
                </button>
              </div>
            </div>
          )}

          <div className="space-y-5 border-t border-white/10 pt-6">
            {fieldVal("bio") && (fieldVal("bio") === "__LOCKED__" ? <Locked label="Story" /> : <Block label="Story">{fieldVal("bio")}</Block>)}
            {fieldVal("offering") && (fieldVal("offering") === "__LOCKED__" ? <Locked label="Offers" /> : <Block label="Offers">{fieldVal("offering")}</Block>)}
            {fieldVal("seeking") && (fieldVal("seeking") === "__LOCKED__" ? <Locked label="Seeks" /> : <Block label="Seeks">{fieldVal("seeking")}</Block>)}
            {fieldVal("link") && fieldVal("link") !== "__LOCKED__" && (
              <a href={/^https?:\/\//i.test(fieldVal("link").trim()) ? fieldVal("link").trim() : `https://${fieldVal("link").trim()}`} target="_blank" rel="noreferrer" data-testid="profile-website-link" className="flex items-center gap-2 text-[#14A89A] hover:text-[#2BC4B5] font-body text-sm break-all"><Link2 size={14} /> {fieldVal("link")}</a>
            )}
            {fieldVal("instagram") && fieldVal("instagram") !== "__LOCKED__" && (
              <a href={`https://instagram.com/${fieldVal("instagram")}`} target="_blank" rel="noreferrer" data-testid="profile-instagram-link" className="flex items-center gap-2 text-[#14A89A] hover:text-[#2BC4B5] font-body text-sm break-all"><Instagram size={14} /> @{fieldVal("instagram")}</a>
            )}
          </div>
        </aside>

        <section className="xl:col-span-2 min-w-0">
          {!isMe && profile.blocked ? (
            <div className="border border-white/10 p-10 text-center" data-testid="profile-blocked-notice">
              <Ban className="mx-auto text-zinc-600 mb-3" size={26} />
              <p className="font-body text-sm text-zinc-400">{profile.blocked_by_me ? "You've blocked this member. Unblock to see their signals again." : "This member is unavailable."}</p>
            </div>
          ) : (<>
          {isMe && (
            <div className="mb-10 border-b border-white/10 pb-10">
              <InnerCircle />
            </div>
          )}

          {(() => {
            const userCollections = (collections || []).filter((c) => c.system !== "stream");
            const streamCollection = (collections || []).find((c) => c.system === "stream");
            const streamCount = streamCollection?.post_count || 0;
            const tabsList = [
              { key: "posts", label: "Posts", count: profile.posts_count, show: true },
              { key: "collections", label: "Collections", count: userCollections.length, show: isMe || userCollections.length > 0 },
              { key: "stream", label: "Conscious Stream", count: streamCount, show: isMe || streamCount > 0 },
            ].filter((t) => t.show);
            const active = tabsList.some((t) => t.key === tab) ? tab : "posts";
            return (
              <>
                <div className="flex items-center gap-1 border-b border-white/10 mb-8 overflow-x-auto" data-testid="profile-tabs">
                  {tabsList.map((t) => (
                    <button key={t.key} onClick={() => setTab(t.key)} data-testid={`profile-tab-${t.key}`} className={`relative px-4 py-3 font-body text-sm whitespace-nowrap transition-colors -mb-px border-b-2 ${active === t.key ? "text-white border-[#14A89A]" : "text-zinc-500 border-transparent hover:text-zinc-300"}`}>
                      {t.label} <span className="text-xs text-zinc-600 ml-0.5">{t.count}</span>
                    </button>
                  ))}
                </div>

                {active === "posts" && (
                  <div data-testid="posts-tab">
                    {profile.posts.length === 0 ? (
                      <p className="font-body text-sm text-zinc-600 py-12">{isMe ? "No signals transmitted yet. Tap Transmit to share your first." : "No signals transmitted yet."}</p>
                    ) : (
                      <div className="flex flex-col gap-12 max-w-xl">
                        {profile.posts.map((p) => (
                          <PostCard key={p.id} post={p} onNav={navigate} onDelete={deletePost} currentUser={user} profileView onChanged={load} />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {active === "collections" && (
                  <div data-testid="collections-tab">
                    {collections === null ? (
                      <div className="flex justify-center py-12"><SignalPulse size={44} /></div>
                    ) : userCollections.length === 0 ? (
                      <div className="border border-dashed border-white/15 p-8 text-center" data-testid="collections-empty">
                        <Layers className="mx-auto text-[#14A89A]/50 mb-4" size={28} />
                        <h3 className="font-heading text-lg text-white mb-2">{isMe ? "Group your posts into a series" : "No collections yet"}</h3>
                        {isMe && <p className="font-body text-sm text-zinc-400 max-w-md mx-auto mb-5">Group your posts into a titled series, a video series, a multi-part teaching, or a running written series. Members can watch or read it start to finish.</p>}
                        {isMe && <NewCollectionControl primary />}
                      </div>
                    ) : (
                      <>
                        {isMe && <div className="mb-6 flex justify-end"><NewCollectionControl /></div>}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {userCollections.map((c) => <CollectionCard key={c.id} c={c} />)}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {active === "stream" && (
                  <div data-testid="stream-tab">
                    {streamCount === 0 ? (
                      <div className="border border-dashed border-white/15 p-8 text-center" data-testid="stream-empty">
                        <Radio className="mx-auto text-[#14A89A]/50 mb-4" size={28} />
                        <h3 className="font-heading text-lg text-white mb-2">Your Conscious Stream</h3>
                        <p className="font-body text-sm text-zinc-400 max-w-md mx-auto">{isMe ? "Share videos and writings from the Conscious Stream and they'll gather here as your personal mini-stream." : "Nothing shared yet."}</p>
                      </div>
                    ) : (!streamDetail || streamDetail._cid !== streamCollection.id || streamDetail._cnt !== streamCollection.post_count) ? (
                      <div className="flex justify-center py-12"><SignalPulse size={44} /></div>
                    ) : (
                      <>
                        {isMe && <div className="mb-6 flex justify-end"><button onClick={() => navigate(`/collection/${streamCollection.id}`)} data-testid="manage-stream-button" className="inline-flex items-center gap-1.5 text-[#14A89A] hover:text-[#2BC4B5] font-body text-xs transition-colors"><Radio size={13} /> Manage stream</button></div>}
                        <div className="flex flex-col gap-12 max-w-xl">
                          {(streamDetail.posts || []).map((p) => (
                            <PostCard key={p.id} post={p} onNav={navigate} onDelete={deleteStreamPost} currentUser={user} onChanged={() => { setStreamDetail(null); reloadCollections(); }} />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            );
          })()}
          </>)}
        </section>
      </div>
    </div>
  );
}
